import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IonicModule, ModalController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import _ from 'lodash';
import moment from 'moment-timezone';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { DatetimeModalComponent } from 'src/app/components/datetime-modal/datetime-modal.component';
import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { DATE_OPTIONS, DATE_RANGE_TYPES } from 'src/app/constants';
import { downloadCSV, getWhereClausesFromFilters } from 'src/app/utils/utils';
import { ApiService } from 'src/services/api.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { SlackBlocksModule } from '../slack-blocks/slack-blocks.module';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    RouterModule,
    NgxDatatableModule,
    SlackBlocksModule,
    NgxTippyModule,
    FilterButtonComponent,
    SurveyAnalyticsPage,
  ],
  selector: 'app-survey-analytics',
  templateUrl: './survey-analytics.page.html',
  styleUrls: ['./survey-analytics.page.scss'],
})
export class SurveyAnalyticsPage implements OnInit {
  @Input() surveyId: string;
  @Input() outboundId?: string;

  survey: any;
  isLoading = true;
  data: any[] = [];
  ColumnMode = ColumnMode;
  hasSurvey: boolean;

  surveyScore = 0;
  totalResponses = 0;
  surveyType?: string;
  surveyResponses: any[] = [];
  surveyScoresByAssignee: any[] = [];

  // Filter-related properties
  dateRange = DATE_OPTIONS[3]; // Default to "Last 90 days"
  startDate: Date | null = null;
  endDate: Date | null = null;
  isDateRangeInvalid = false;

  constructor(
    private apiService: ApiService,
    private modalCtrl: ModalController,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    this.getSurveyData();
  }

  async getSurveyData() {
    this.isLoading = true;

    // Build query parameters from filters
    const whereClause = this.buildWhereClause();
    const queryParams = this.buildQueryParams();
    const baseUrl = `/surveys/${this.surveyId}/analytics`;
    const hasOutboundId = !!this.outboundId;

    let analyticsUrl = baseUrl;
    if (hasOutboundId || queryParams.length > 0) {
      analyticsUrl += '?';
      if (hasOutboundId) {
        analyticsUrl += `outboundId=${this.outboundId}`;
        if (queryParams.length > 0) {
          analyticsUrl += '&' + queryParams.join('&');
        }
      } else {
        analyticsUrl += queryParams.join('&');
      }
    }

    const [surveyResponse, surveyResponsesResponse]: [any, any] = await Promise.all([
      this.apiService.getPromise(analyticsUrl),
      this.apiService.postPromise(
        `/surveys/${this.surveyId}/responses/list${this.outboundId ? `?outboundId=${this.outboundId}` : ''}`,
        {
          limit: null,
          order: ['createdAt'],
          descending: true,
          where: whereClause,
        },
      ),
    ]);

    this.survey = surveyResponse.survey;
    this.totalResponses = surveyResponse.totalResponses;
    this.surveyScore = surveyResponse.score;
    this.surveyScoresByAssignee = surveyResponse.byAssignee;
    this.surveyResponses = surveyResponsesResponse.data;

    this.isLoading = false;
  }

  private buildQueryParams(): string[] {
    const params: string[] = [];
    // Handle date range
    if (this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE && this.startDate && this.endDate) {
      params.push(`startDate=${this.startDate.toISOString()}`);
      params.push(`endDate=${this.endDate.toISOString()}`);
    } else if (
      this.dateRange.type === DATE_RANGE_TYPES.RELATIVE &&
      this.dateRange.period &&
      this.dateRange.startIndex !== undefined
    ) {
      let startMoment: moment.Moment;
      let endMoment: moment.Moment;

      if (this.dateRange.startIndex === 0 && this.dateRange.endIndex === 0) {
        // Handle "To Date" periods (WTD, MTD, QTD, YTD)
        if (this.dateRange.period === 'week') {
          startMoment = moment().startOf('week');
          endMoment = moment();
        } else if (this.dateRange.period === 'month') {
          startMoment = moment().startOf('month');
          endMoment = moment();
        } else if (this.dateRange.period === 'quarter') {
          startMoment = moment().startOf('quarter');
          endMoment = moment();
        } else if (this.dateRange.period === 'year') {
          startMoment = moment().startOf('year');
          endMoment = moment();
        }
      } else {
        // Handle regular "Last X" periods
        endMoment = moment();
        startMoment = moment().subtract(
          this.dateRange.startIndex,
          this.dateRange.period as moment.unitOfTime.DurationConstructor,
        );
      }

      if (startMoment && endMoment) {
        params.push(`startDate=${startMoment.toISOString()}`);
        params.push(`endDate=${endMoment.toISOString()}`);
      }
    }

    return params;
  }

  private buildWhereClause(): any[] {
    const whereClause = [];

    // Add date filter to where clause based on dateRange
    if (this.dateRange.type === DATE_RANGE_TYPES.ABSOLUTE && this.startDate && this.endDate) {
      whereClause.push({
        field: 'createdAt',
        operator: '>=',
        value: this.startDate.toISOString(),
      });
      whereClause.push({
        field: 'createdAt',
        operator: '<=',
        value: this.endDate.toISOString(),
      });
    } else if (
      this.dateRange.type === DATE_RANGE_TYPES.RELATIVE &&
      this.dateRange.period &&
      this.dateRange.startIndex !== undefined
    ) {
      let startMoment: moment.Moment;
      let endMoment: moment.Moment;

      if (this.dateRange.startIndex === 0 && this.dateRange.endIndex === 0) {
        // Handle "To Date" periods (WTD, MTD, QTD, YTD)
        if (this.dateRange.period === 'week') {
          startMoment = moment().startOf('week');
          endMoment = moment();
        } else if (this.dateRange.period === 'month') {
          startMoment = moment().startOf('month');
          endMoment = moment();
        } else if (this.dateRange.period === 'quarter') {
          startMoment = moment().startOf('quarter');
          endMoment = moment();
        } else if (this.dateRange.period === 'year') {
          startMoment = moment().startOf('year');
          endMoment = moment();
        }
      } else {
        // Handle regular "Last X" periods
        endMoment = moment();
        startMoment = moment().subtract(
          this.dateRange.startIndex,
          this.dateRange.period as moment.unitOfTime.DurationConstructor,
        );
      }

      if (startMoment && endMoment) {
        whereClause.push({
          field: 'createdAt',
          operator: '>=',
          value: startMoment.toISOString(),
        });
        whereClause.push({
          field: 'createdAt',
          operator: '<=',
          value: endMoment.toISOString(),
        });
      }
    }

    return whereClause;
  }

  async showDateRangeOptions(event: any) {
    this.searchablePopoverService.dateValues({
      event,
      callback: async (_id: string, _name: string, object: any) => {
        this.dateRange = object;

        if (object.type !== DATE_RANGE_TYPES.ABSOLUTE) {
          await this.getSurveyData();
        } else {
          // For absolute date range, clear existing dates and wait for user to select them
          this.startDate = null;
          this.endDate = null;
          this.isDateRangeInvalid = false;
        }
      },
    });
  }

  async openStartDatePicker() {
    const presentingElement = await this.modalCtrl.getTop();

    presentingElement.canDismiss = false;

    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        selected: this.startDate || new Date(),
        max: this.endDate || new Date(),
        title: 'Select start date',
        presentation: 'date',
      },
      presentingElement,
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then(() => {
      presentingElement.canDismiss = true;
    });

    const { data } = await datePickerModal.onDidDismiss();
    const result = data?.value;

    if (result && result !== 'Invalid date') {
      this.startDate = new Date(result);
      this.validateAndRefreshData();
    }
  }

  async openEndDatePicker() {
    const presentingElement = await this.modalCtrl.getTop();

    presentingElement.canDismiss = false;

    const datePickerModal = await this.modalCtrl.create({
      component: DatetimeModalComponent,
      cssClass: 'datetime-modal',
      componentProps: {
        selected: this.endDate || new Date(),
        min: this.startDate || undefined,
        title: 'Select end date',
        presentation: 'date',
      },
      presentingElement,
    });

    await datePickerModal.present();

    datePickerModal.onDidDismiss().then(() => {
      presentingElement.canDismiss = true;
    });

    const { data } = await datePickerModal.onDidDismiss();
    const result = data?.value;

    if (result && result !== 'Invalid date') {
      this.endDate = new Date(result);
      this.validateAndRefreshData();
    }
  }

  private validateAndRefreshData() {
    if (this.startDate && this.endDate) {
      this.isDateRangeInvalid = this.startDate.getTime() > this.endDate.getTime();

      if (!this.isDateRangeInvalid) {
        this.getSurveyData();
      }
    } else {
      this.isDateRangeInvalid = false;
    }
  }

  getPercentageScore(value: number) {
    return this.survey.type === 'nps' ? value / 10 : value / 5;
  }

  downloadAsCsv() {
    const headers = ['id', 'createdAt', 'name', 'value', 'comment', 'conversationId', 'userId'];
    const results = this.surveyResponses.map((response) => {
      return {
        id: response.id,
        createdAt: response.createdAt,
        name: response.user.name,
        value: response.value,
        comment: response.comment,
        conversationId: response.conversationId,
        userId: response.userId,
      };
    });

    downloadCSV(results, headers, `survey-responses-${this.surveyId}-${this.outboundId}`);
  }
}
